import type {RteValue} from "./rte-value.type";
import type {LinkTile} from "./entities/link-tile.entity";
import {LinkTargetSerializer} from "./entities/link-targets/link-target-serializer";
import {LinkTileHelper} from "../helpers/link-tile.helper";

/**
 * One tile as the backend hands it out: the same fields, but the identity is
 * called `uid` and the link target is a raw JSON STRING rather than an object,
 * because the column it lives in is a text column the backend never decodes.
 *
 * `text` is a raw string too, for the identical reason — the column never
 * decodes it either. Since 2026-09-07 it holds a JSON-stringified Quill
 * Delta (`{"ops":[...]}`) rather than a plain label; a row written before
 * that (a bare label string, not JSON — or JSON with no `ops`) is migrated on
 * read, see `toDomain()`'s `parseText()`. `textColor` is READ here (never
 * written by `toPayload()` any more, see its own comment) purely so a
 * legacy row's colour survives that one-time migration instead of being
 * silently dropped — once a tile round-trips through `toPayload()` its
 * colour lives in the Delta itself and `textColor` is never consulted again.
 */
export interface LinkTileRow {
    readonly uid: string;
    readonly text: string;
    readonly textColor?: string | null;
    readonly color?: string | null;
    readonly icon?: string | null;
    readonly target?: string | null;
    /** The asset-store reference, an opaque ShortId the backend never converts. */
    readonly imageAssetUid?: string | null;
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
}

/**
 * One tile as it goes onto the wire, inside a create or update command. The
 * identity is `uid` again, and the target is a string again.
 *
 * The parent page is deliberately NOT here: only the create command carries a
 * `linkviewUid`, and it is a property of the command rather than of the tile,
 * so the caller adds it. Putting it here would force every update payload to
 * carry a field the backend does not read.
 *
 * `textColor` is gone (Anke, 2026-09-07: the stripped-down RTE folds colour
 * into the Delta's own `color`/`background` ops — there is no more separate
 * field to send). The backend column still exists but is never written by
 * this mapper any more; `LinkTileRow.textColor` above stays readable so an
 * already-stored value migrates forward exactly once.
 */
export interface LinkTileWirePayload {
    readonly uid: string;
    readonly text: string;
    readonly color?: string;
    readonly icon?: string;
    readonly target?: string;
    readonly imageAssetUid?: string;
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
}

/**
 * THE ONE PLACE where `uid` and `id` are translated into each other.
 *
 * The rename has to be written down somewhere, and if it is written down in
 * more than one place the two copies drift. Worse, if it is written down
 * nowhere the failure is silent: the field is simply absent from the serialized
 * object, the tile arrives with `id === undefined`, and every lookup by id
 * misses. There is no type-check gate on this build — the bundler transpiles
 * and does not check — so nothing catches it before a user does.
 */
export function toDomain(row: LinkTileRow): LinkTile {
    const target = parseTarget(row.target);

    return {
        id: row.uid,
        text: parseText(row.text, row.textColor),
        ...(target ? {target} : {}),
        ...(row.color ? {color: row.color} : {}),
        ...(row.icon ? {icon: row.icon} : {}),
        ...(row.imageAssetUid ? {imageAssetUid: row.imageAssetUid} : {}),
        x: row.x,
        y: row.y,
        w: row.w,
        h: row.h,
    };
}

/**
 * Every optional field is conditionally spread rather than assigned
 * `undefined`, matching how a tile is built in the first place: an unset field
 * must be genuinely absent, not present-and-undefined, or it reaches the
 * backend as an explicit null and clears something the user never touched.
 *
 * `text` is stringified exactly like `target` below it — same reasoning, same
 * pattern: the column is a plain string the backend never decodes, so
 * losslessness is the CLIENT's job. `textColor` is never sent any more (see
 * `LinkTileWirePayload`'s own comment) — every save from here on carries the
 * colour inside the Delta itself.
 *
 * `JSON.stringify(tile.text)` (not `{ops: tile.text.ops}`, unlike before the
 * RTE unification) serializes EITHER shape of `RteValue` correctly as-is: a
 * `Delta` instance's own `ops` field is a plain enumerable property, and a
 * `RichTextDocument` is already plain JSON — wrapping in `{ops: ...}` would
 * silently lose a `RichTextDocument`'s `type`/`content`.
 */
export function toPayload(tile: LinkTile): LinkTileWirePayload {
    return {
        uid: tile.id,
        text: JSON.stringify(tile.text),
        ...(tile.color ? {color: tile.color} : {}),
        ...(tile.icon ? {icon: tile.icon} : {}),
        ...(tile.target ? {target: JSON.stringify(tile.target.toJSON())} : {}),
        ...(tile.imageAssetUid ? {imageAssetUid: tile.imageAssetUid} : {}),
        x: tile.x,
        y: tile.y,
        w: tile.w,
        h: tile.h,
    };
}

/**
 * Revives `row.text` into a Delta, migrating whatever shape a pre-2026-09-07
 * row still carries — applied on EVERY read (same "migrate on read, self-heal
 * on the next save" stance `parseTarget`'s legacy-`url` sibling in
 * `LocalStorageLinkviewRepository` already uses): a tile that is only ever
 * viewed, never re-saved, would otherwise keep the legacy shape forever.
 *
 * The actual shape-detection (JSON-encoded Delta vs. bare legacy label,
 * `row.textColor` folded in for the latter) lives in
 * `LinkTileHelper.migrateText()` — review 0030-01 (INFO) flagged this
 * function and `LocalStorageLinkviewRepository`'s `migrateText()` as two
 * independently-maintained copies of the same logic; both now delegate to
 * the one shared implementation.
 */
function parseText(raw: string, legacyTextColor: string | null | undefined): RteValue {
    return LinkTileHelper.migrateText(raw, legacyTextColor);
}

/**
 * Revives the stored string into a target object.
 *
 * What is guaranteed here is VALUE fidelity, not byte fidelity, and the
 * difference is worth stating because the column's guarantee is the stronger
 * one: the backend stores exactly the bytes it received, because it never
 * parses them. Across this client round trip the string is parsed and
 * re-stringified, so insignificant formatting — whitespace, key order — can
 * differ. No key and no value is lost, which is what the lossless fallback for
 * an unrecognised kind actually promises.
 *
 * Note this is the opposite of the hazard on the PHP side: in JavaScript `{}`
 * parses and re-stringifies as `{}`, and an object with numeric string keys
 * stays an object. The collapse into an array is specifically a PHP
 * associative-decode behaviour, which is why the column is text there.
 *
 * A string that is not a JSON object at all leaves the tile without a target:
 * the lossless fallback needs an object to carry, and there is nothing
 * meaningful to build from a bare scalar. It cannot arise from anything this
 * mapper wrote.
 */
function parseTarget(raw: string | null | undefined): LinkTile["target"] {
    if (!raw) {
        return undefined;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return undefined;
    }

    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return undefined;
    }

    return LinkTargetSerializer.fromJSON(parsed as Record<string, unknown>);
}
